import { useQuery } from '@powersync/react';
import { useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { RunRow } from '@/components/domain/RunRow';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { Bounded } from '@/components/ui/Bounded';
import { BoundedScrollView } from '@/components/ui/BoundedScrollView';
import { EmptyState } from '@/components/ui/EmptyState';
import { NavBar } from '@/components/ui/NavBar';
import { Screen } from '@/components/ui/Screen';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Surface } from '@/components/ui/Surface';
import { Text } from '@/components/ui/Text';
import { type Instance } from '@/lib/data';
import { useGoBack } from '@/lib/navigation';
import { usePendingInstanceIds } from '@/lib/sync-status';

export default function RunsScreen() {
  const goBack = useGoBack('/');
  const syncScrollRef = useAnimatedRef<Animated.ScrollView>();
  const { data: instances, isLoading } = useQuery<Instance>(
    'SELECT * FROM instances ORDER BY started_at DESC',
  );
  const pendingIds = usePendingInstanceIds();

  const { active, completed } = useMemo(
    () => ({
      active: instances.filter((i) => i.status === 'active'),
      completed: instances.filter((i) => i.status === 'completed'),
    }),
    [instances],
  );

  return (
    <Screen edges={['top']}>
      <NavBar onBack={goBack} title="History" />

      <SyncStatusArea scrollRef={syncScrollRef}>
        {!isLoading && instances.length === 0 ? (
          <Bounded className="flex-1 justify-center">
            <EmptyState
              icon="📒"
              title="No runs yet"
              body="Start a run from one of your templates — completed runs are sealed here as a permanent record."
            />
          </Bounded>
        ) : (
          <BoundedScrollView
            scrollRef={syncScrollRef}
            alwaysBounceVertical
            contentContainerClassName="gap-6 px-5 pb-10 pt-2"
          >
            {active.length > 0 ? (
              <View className="gap-3">
                <SectionHeader label="Active" count={active.length} />
                <Surface className="overflow-hidden">
                  {active.map((i, idx) => (
                    <View key={i.id}>
                      {idx > 0 ? <View className="ml-4 h-px bg-border" /> : null}
                      <RunRow instance={i} href={`/instance/${i.id}`} />
                    </View>
                  ))}
                </Surface>
              </View>
            ) : null}

            {completed.length > 0 ? (
              <View className="gap-3">
                <SectionHeader label="Sealed" count={completed.length} />
                <Surface className="overflow-hidden">
                  {completed.map((i, idx) => (
                    <View key={i.id}>
                      {idx > 0 ? <View className="ml-4 h-px bg-border" /> : null}
                      <RunRow
                        instance={i}
                        href={`/instance/${i.id}`}
                        unsynced={pendingIds.has(i.id)}
                      />
                    </View>
                  ))}
                </Surface>
              </View>
            ) : (
              <Text variant="caption" className="px-1">
                No sealed runs yet.
              </Text>
            )}
          </BoundedScrollView>
        )}
      </SyncStatusArea>
    </Screen>
  );
}
